import type { OccupancySeasonHeatmapCell } from '@sea-snag/shared';

/**
 * Row order for the occupancy grid.
 *
 * Sorting happens here, on the client, and never reaches the API. That is what
 * makes the brief's requirement — "sorting must not change metrics" — true by
 * construction rather than something to verify: the summary cards, the season
 * breakdown and the occupancy percentage are all computed server-side from the
 * filtered boat set, which sorting has no way to touch.
 *
 * Booked days come from the heatmap cells already in the payload, so the order
 * agrees with the grid it reorders and with the card above it. Reading them from
 * anywhere else would let the sort disagree with what the rows visibly show.
 */

export const OCCUPANCY_SORTS = ['weeks-desc', 'weeks-asc', 'name-asc'] as const;
export type OccupancySort = (typeof OCCUPANCY_SORTS)[number];

/** The brief's default: busiest boats first. */
export const DEFAULT_OCCUPANCY_SORT: OccupancySort = 'weeks-desc';

export function isOccupancySort(value: unknown): value is OccupancySort {
  return typeof value === 'string' && (OCCUPANCY_SORTS as readonly string[]).includes(value);
}

/**
 * Confirmed booked days per boat. Only `bookedDays` counts — weeks sold through
 * other channels and maintenance blocks are drawn on the grid but are not ours,
 * and the booked-weeks card excludes them too.
 */
export function bookedDaysByBoat(heatmap: OccupancySeasonHeatmapCell[]): Map<string, number> {
  const byBoat = new Map<string, number>();
  for (const cell of heatmap) {
    byBoat.set(cell.boatPid, (byBoat.get(cell.boatPid) ?? 0) + cell.bookedDays);
  }
  return byBoat;
}

export function sortOccupancyBoats<T extends { pid: string; name: string }>(
  boats: T[],
  bookedDays: Map<string, number>,
  sort: OccupancySort
): T[] {
  const byName = (a: T, b: T) => a.name.localeCompare(b.name);
  const days = (b: T) => bookedDays.get(b.pid) ?? 0;

  // Copied before sorting: these rows come from the query cache, and sorting in
  // place would reorder cached data so the order depended on render count.
  return [...boats].sort((a, b) => {
    if (sort === 'name-asc') return byName(a, b);
    const diff = sort === 'weeks-desc' ? days(b) - days(a) : days(a) - days(b);
    // Name breaks ties so equally-booked boats hold a stable order. Array sort
    // is only stable within a single call, so without this two such boats could
    // swap places between renders for no visible reason.
    return diff !== 0 ? diff : byName(a, b);
  });
}
